/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useOrganizationTree.ts
 * PURPOSE: TanStack Query hook fetching tenant organization tree (8-layer hierarchy)
 *          for AnchorPicker cascading dropdown UI.
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-04-29
 * COMPLIANCE: SDLC 6.3.1 + DD-0051 v0.3.1 D1 + BR-TENM-013 v0.2.1 REQ-I
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (React Best Practices)
 * SPRINT GOVERNANCE: Rule 6/7: Performance SLAs (5min staleTime cache)
 *
 * @business-context
 * - Domain: Tenant Admin, Multi-Tenant Org Hierarchy (Tập đoàn → Team)
 * - Impact: Operational — supports mandatory organization_id selection per DD-0051
 * - Stakeholders: Tenant admins, branch managers
 *
 * @technical-architecture
 * - Layer: Data Access (hook layer)
 * - Pattern: TanStack Query v5 (useQuery), tree assembly via parent_id grouping
 * - Dependencies: @tanstack/react-query v5, axios apiClient
 *
 * @cross-references
 * - BR: docs/01-planning/01-requirements/03-tenant-management/BR-TENM-013-USER-ANCHOR-LIFECYCLE.md (REQ-I)
 * - DD: docs/02-design/18-adrs/06-decisions/DD-0051-tenant-user-org-anchor.md (D1)
 * - BR: docs/01-planning/01-requirements/02-platform-admin/BR-PADM-008-ORG-SCOPE-ENFORCEMENT.md (REQ-G)
 * - API: backend/app/api/v1/organizations.py (GET /api/v1/organizations)
 * - Component: frontend/src/pages/tenant-admin/components/AnchorPicker.tsx
 */

import { useMemo } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiClient } from "../services/api/client";

// ISS-0014 (2026-07-29, @tester qua E2E USER-01 + vite dev): fallback `?? []`
// tạo MẢNG MỚI mỗi render khi data chưa về/lỗi → identity đổi liên tục →
// useEffect phụ thuộc (orgLevelById ở TenantUserManagement) chạy lại vô hạn
// → "Maximum update depth exceeded" → crash trang (React #300 ở prod build).
// Module-level const giữ identity ổn định.
const EMPTY_ORG_LIST: OrganizationNode[] = [];

/**
 * Vietnamese 8-layer organization hierarchy levels per CLAUDE.md.
 * L1=Tập đoàn (Group), L2=Chuỗi (Chain), L3=Brand, L4=Region,
 * L5=Chi nhánh (Branch), L6=Work Area, L7=Shift, L8=Team.
 */
export type OrgLevel = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface OrganizationNode {
  id: string;
  name: string;
  name_vietnamese?: string | null;
  code: string;
  parent_id: string | null;
  level: OrgLevel;
  is_active: boolean;
  tenant_id?: string;
}

export interface OrganizationTreeNode extends OrganizationNode {
  children: OrganizationTreeNode[];
}

export interface UseOrganizationTreeResult {
  tree: OrganizationTreeNode[];
  flat: OrganizationNode[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: UseQueryResult["refetch"];
}

/**
 * Build hierarchical tree from flat node array via parent_id grouping.
 * Roots = nodes with parent_id == null. Children sorted by level then name.
 */
function buildTree(nodes: OrganizationNode[]): OrganizationTreeNode[] {
  const byId = new Map<string, OrganizationTreeNode>();
  nodes.forEach((n) => byId.set(n.id, { ...n, children: [] }));

  const roots: OrganizationTreeNode[] = [];
  byId.forEach((node) => {
    if (node.parent_id && byId.has(node.parent_id)) {
      byId.get(node.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortFn = (a: OrganizationTreeNode, b: OrganizationTreeNode) =>
    a.level - b.level || a.name.localeCompare(b.name, "vi");

  const sortChildren = (n: OrganizationTreeNode) => {
    n.children.sort(sortFn);
    n.children.forEach(sortChildren);
  };
  roots.sort(sortFn);
  roots.forEach(sortChildren);

  return roots;
}

/**
 * Fetch tenant organization tree.
 *
 * @param tenantId - tenant scope (passed as query string for tenant-admin endpoint)
 * @returns hierarchical tree + flat list, with TanStack Query state
 *
 * Cache policy: 5 min staleTime — org tree changes infrequently, avoid redundant
 * fetches across multiple form mounts (Create + Edit modals reuse same data).
 */
export function useOrganizationTree(
  tenantId?: string,
  options?: { enabled?: boolean }
): UseOrganizationTreeResult {
  const enabled = options?.enabled ?? true;

  const query = useQuery<OrganizationNode[]>({
    queryKey: ["organizations", "tree", tenantId ?? "current"],
    queryFn: async () => {
      const params: Record<string, unknown> = { is_active: true, limit: 1000 };
      if (tenantId) params.tenant_id = tenantId;
      const response = await apiClient.get("/organizations/", { params });
      return Array.isArray(response.data) ? response.data : response.data?.items ?? [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes per spec
    gcTime: 10 * 60 * 1000,
    retry: 2,
    enabled,
  });

  const flat = query.data ?? EMPTY_ORG_LIST;
  const tree = useMemo(() => buildTree(flat), [flat]);

  return {
    tree,
    flat,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
